/* VIYAN: two-slot photo-tile selection for the complimentary charm picker.

   Layout (2026-09-16): two slot cards ("Charm 1" / "Charm 2") on top and
   ONE shared grid of <button role="radio"> tiles below (symbols and
   letters together). The picker root carries data-viyan-active-slot; a
   tile click writes that slot's hidden input
   (name="properties[Gift Charm N]"), which is what travels to the cart.
   After Charm 1 is filled the active slot moves to Charm 2 on its own;
   tapping a slot card makes it active again so it can be changed. The
   same charm may be picked for both slots.

   The hidden inputs keep data-viyan-charm-select, so viyan-charm-picker.js
   gates add-to-cart exactly as before. After every change we dispatch a
   bubbling 'change' event on the input so that script clears the error
   banner and drops the wallet-button shield. viyan-charm-color-filter.js
   clears Silver values the same way; the change listener below re-renders
   from the inputs, so the cards, badges and thumbnails follow it.

   Listeners are document-level delegation: they survive Dawn re-rendering
   the product form after a variant change. All visible state is derived
   from the hidden inputs in render(), never stored separately. */
(function () {
  'use strict';

  var PICKER = '[data-viyan-charm-picker]';
  var FIELD = '[data-viyan-charm-field]';
  var TILE = '[data-viyan-charm-tile]';
  var INPUT = '[data-viyan-charm-select]';
  var EMPTY_TEXT = 'Not selected yet';
  var ERROR_CLASS = 'viyan-charm-picker__field--error';

  /* Set while this script dispatches its own change event, so the
     listener can tell a tile click from an outside clear. */
  var committing = false;

  function toArray(list) {
    return Array.prototype.slice.call(list);
  }

  function fieldsOf(picker) {
    return toArray(picker.querySelectorAll(FIELD));
  }

  function tilesOf(picker) {
    return toArray(picker.querySelectorAll(TILE));
  }

  function valueOf(field) {
    var input = field.querySelector(INPUT);
    return input ? input.value : '';
  }

  function activeIndex(picker) {
    var count = fieldsOf(picker).length;
    var n = parseInt(picker.getAttribute('data-viyan-active-slot'), 10);
    return n >= 1 && n <= count ? n - 1 : 0;
  }

  function firstEmptyIndex(picker) {
    var fields = fieldsOf(picker);
    for (var i = 0; i < fields.length; i++) {
      if (!valueOf(fields[i])) return i;
    }
    return -1;
  }

  function tileFor(picker, value) {
    if (!value) return null;
    return tilesOf(picker).filter(function (tile) {
      return tile.getAttribute('data-viyan-charm-value') === value;
    })[0] || null;
  }

  function renderThumb(field, tile) {
    var thumb = field.querySelector('[data-viyan-charm-thumb]');
    if (!thumb) return;
    var source = tile ? tile.querySelector('img') : null;
    var src = source ? source.currentSrc || source.src : '';
    var current = thumb.querySelector('img');
    if (current && current.getAttribute('src') === src) return;
    thumb.textContent = '';
    if (!src) return;
    var img = document.createElement('img');
    img.src = src;
    img.alt = '';
    img.width = 96;
    img.height = 96;
    img.decoding = 'async';
    thumb.appendChild(img);
  }

  function renderFields(picker, active) {
    fieldsOf(picker).forEach(function (field, index) {
      var value = valueOf(field);
      var isActive = index === active;
      field.classList.toggle('is-active', isActive);
      var button = field.querySelector('[data-viyan-charm-slot-button]');
      if (button) button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      var readout = field.querySelector('[data-viyan-charm-readout]');
      if (readout) {
        readout.textContent = value || EMPTY_TEXT;
        readout.classList.toggle('is-set', !!value);
      }
      renderThumb(field, tileFor(picker, value));
    });
  }

  function renderTiles(picker, active) {
    var values = fieldsOf(picker).map(valueOf);
    var activeValue = values[active] || '';
    var tiles = tilesOf(picker);
    var tabStop = null;

    tiles.forEach(function (tile) {
      var value = tile.getAttribute('data-viyan-charm-value');
      var picked = [];
      values.forEach(function (v, i) {
        if (v && v === value) picked.push(String(i + 1));
      });
      if (picked.length) tile.setAttribute('data-viyan-charm-picked', picked.join(' · '));
      else tile.removeAttribute('data-viyan-charm-picked');

      var checked = !!activeValue && value === activeValue;
      tile.setAttribute('aria-checked', checked ? 'true' : 'false');
      if (checked && !tile.hidden) tabStop = tile;
    });

    /* Roving tabindex: one tab stop for the whole grid. */
    if (!tabStop) {
      tabStop = tiles.filter(function (tile) { return !tile.hidden; })[0] || null;
    }
    tiles.forEach(function (tile) {
      tile.tabIndex = tile === tabStop ? 0 : -1;
    });
  }

  function renderHint(picker, active) {
    var hint = picker.querySelector('[data-viyan-charm-hint]');
    if (!hint) return;
    hint.textContent = firstEmptyIndex(picker) === -1
      ? 'Both charms chosen — tap Charm 1 or Charm 2 to change'
      : 'Tap a charm below to fill Charm ' + (active + 1);
  }

  function render(picker) {
    var active = activeIndex(picker);
    renderFields(picker, active);
    renderTiles(picker, active);
    renderHint(picker, active);
  }

  function setActive(picker, index) {
    picker.setAttribute('data-viyan-active-slot', String(index + 1));
    render(picker);
  }

  function selectTile(tile) {
    var picker = tile.closest(PICKER);
    if (!picker) return;
    var active = activeIndex(picker);
    var field = fieldsOf(picker)[active];
    var input = field ? field.querySelector(INPUT) : null;
    if (!input) return;

    input.value = tile.getAttribute('data-viyan-charm-value') || '';
    field.classList.remove(ERROR_CLASS);
    committing = true;
    try {
      input.dispatchEvent(new Event('change', { bubbles: true }));
    } finally {
      committing = false;
    }

    /* Charm 1 done → move on to the next empty slot. When both are set,
       stay on the slot being edited. */
    var nextEmpty = firstEmptyIndex(picker);
    setActive(picker, nextEmpty === -1 ? active : nextEmpty);
  }

  document.addEventListener('click', function (event) {
    if (!event.target || !event.target.closest) return;

    var tile = event.target.closest(TILE);
    if (tile) {
      event.preventDefault();
      selectTile(tile);
      return;
    }

    var slotButton = event.target.closest('[data-viyan-charm-slot-button]');
    if (!slotButton) return;
    var picker = slotButton.closest(PICKER);
    var field = slotButton.closest(FIELD);
    if (!picker || !field) return;
    event.preventDefault();
    setActive(picker, fieldsOf(picker).indexOf(field));
  });

  /* Hidden inputs changed from outside (color filter clearing a Silver
     charm, bfcache): re-render, and if the active slot is already filled
     while another is empty, point the grid at the empty one. */
  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input || !input.matches || !input.matches(INPUT)) return;
    var picker = input.closest(PICKER);
    if (!picker || committing) return;
    var empty = firstEmptyIndex(picker);
    var active = activeIndex(picker);
    if (empty !== -1 && valueOf(fieldsOf(picker)[active])) {
      setActive(picker, empty);
    } else {
      render(picker);
    }
  });

  /* Arrow keys move focus through the visible tiles; Enter / Space use the
     button's native click, which selects. */
  document.addEventListener('keydown', function (event) {
    if (!event.target || !event.target.closest) return;
    var tile = event.target.closest(TILE);
    if (!tile) return;

    var step = 0;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') step = 1;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') step = -1;
    else return;

    var picker = tile.closest(PICKER);
    if (!picker) return;
    event.preventDefault();
    var items = tilesOf(picker).filter(function (t) { return !t.hidden; });
    var index = items.indexOf(tile);
    if (index < 0) return;
    var next = items[(index + step + items.length) % items.length];
    items.forEach(function (t) { t.tabIndex = t === next ? 0 : -1; });
    next.focus();
  });

  function init() {
    document.querySelectorAll(PICKER).forEach(function (picker) {
      var empty = firstEmptyIndex(picker);
      setActive(picker, empty === -1 ? 0 : empty);
    });
  }
  init();
  /* bfcache restore brings back the hidden input values but not the
     rendered cards, so rebuild everything from the inputs. */
  window.addEventListener('pageshow', init);
})();
